import { Injectable, signal } from "@angular/core";
import { environment } from "./environment";

const RESYNC_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Lightweight server-time offset helper. Corrects device clock skew so
 * pre-PartyKit countdowns (lobby, live pill, resume banner) stay aligned
 * with server time. PartyKit timer_tick is already server-authoritative
 * and unaffected by this offset.
 */
@Injectable({ providedIn: "root" })
export class TimeService {
	private offsetMs = 0;
	readonly lastSyncedAt = signal<number | null>(null);

	private inFlight: Promise<void> | null = null;
	private resyncTimer: ReturnType<typeof setInterval> | null = null;
	private listenersBound = false;

	async initialize(): Promise<void> {
		await this.sync();
		if (typeof window !== "undefined" && !this.listenersBound) {
			window.addEventListener("focus", () => this.sync());
			this.resyncTimer = setInterval(() => this.sync(), RESYNC_INTERVAL_MS);
			this.listenersBound = true;
		}
	}

	serverNow(): number {
		return Date.now() + this.offsetMs;
	}

	serverDate(): Date {
		return new Date(this.serverNow());
	}

	/**
	 * One-shot sync. Safe to call repeatedly — concurrent calls dedupe.
	 */
	async sync(): Promise<void> {
		if (this.inFlight) return this.inFlight;
		this.inFlight = this.fetchOffset().finally(() => {
			this.inFlight = null;
		});
		return this.inFlight;
	}

	private async fetchOffset(): Promise<void> {
		try {
			const sentAt = Date.now();
			const res = await fetch(`${environment.serverUrl}/api/time`, {
				method: "GET",
			});
			if (!res.ok) return;
			const payload = (await res.json()) as {
				status?: string;
				data?: { now?: number; iso?: string };
			};
			const serverNow = payload.data?.now;
			if (typeof serverNow !== "number") return;

			// Correct for round-trip: server timestamp was taken roughly halfway
			// through the request. Close enough for a single-sample sync.
			const receivedAt = Date.now();
			const rttMs = receivedAt - sentAt;
			const estimatedServerNow = serverNow + rttMs / 2;
			this.offsetMs = estimatedServerNow - receivedAt;
			this.lastSyncedAt.set(receivedAt);
		} catch {
			// Network errors are silent — offsetMs stays at its last known value
			// (or 0 if we've never synced).
		}
	}
}
